import { supabase } from "@/integrations/supabase/client";
import { demoStore } from "./demo-data";
import type { Customer, Job, JobActivity, JobFile, JobStatus } from "./types";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export interface DataSource {
  listJobs(filter?: { customer_id?: string; includeArchived?: boolean }): Promise<Job[]>;
  getJob(id: string): Promise<Job | null>;
  createJob(input: Partial<Job> & { customer_id: string; job_title: string }): Promise<Job>;
  updateJobStatus(id: string, status: JobStatus): Promise<void>;
  updateJob(id: string, patch: Partial<Job>): Promise<void>;
  archiveJob?(id: string): Promise<void>;
  unarchiveJob?(id: string): Promise<void>;
  deleteJob?(id: string): Promise<void>;
  listFiles(jobId: string): Promise<JobFile[]>;
  addFile(jobId: string, file: File): Promise<JobFile>;
  updateFileType(
    fileId: string,
    fileType: "artwork" | "proof" | "gang_sheet" | "other",
  ): Promise<void>;
  listActivity(jobId: string): Promise<JobActivity[]>;
  listCustomers(): Promise<Customer[]>;
  getCustomer(id: string): Promise<Customer | null>;
}

const supaSource: DataSource = {
  async listJobs(filter) {
    let q = supabase
      .from("jobs")
      .select("*, customer:customers(*)")
      .order("updated_at", { ascending: false });
    if (filter?.customer_id) q = q.eq("customer_id", filter.customer_id);
    if (!filter?.includeArchived) q = q.is("archived_at", null);
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as unknown as Job[];
  },
  async getJob(id) {
    const { data } = await supabase
      .from("jobs")
      .select("*, customer:customers(*)")
      .eq("id", id)
      .maybeSingle();
    return (data as unknown as Job | null) ?? null;
  },
  async createJob(input) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const insertPayload: any = {
      customer_id: input.customer_id,
      job_title: input.job_title,
      product_type: input.product_type ?? null,
      print_size: input.print_size ?? null,
      quantity: input.quantity ?? 1,
      colours: input.colours ?? null,
      notes: input.notes ?? null,
      due_date: input.due_date ?? null,
      status: (input.status as JobStatus) ?? "New",
      cutting_tier_id: input.cutting_tier_id ?? null,
      shipping_method_id: input.shipping_method_id ?? null,
      gangsheet_design_id: input.gangsheet_design_id ?? null,
      payment_status: input.payment_status ?? "pending",
      assignee_profile_id: input.assignee_profile_id ?? null,
      drip_size_id: input.drip_size_id ?? null,
      metres: input.metres ?? null,
      quality_status: input.quality_status ?? null,
    };
    const { data, error } = await supabase.from("jobs").insert(insertPayload).select("*").single();
    if (error) throw error;

    const job = data as unknown as Job;

    // TODO: Create notification for the customer who created the job
    // This requires mapping customer_id to user_id, which will be implemented
    // when the customer-user relationship is established in the database

    return job;
  },
  async updateJobStatus(id, status) {
    const { error } = await supabase.from("jobs").update({ status }).eq("id", id);
    if (error) throw error;

    // Create notification for customer
    // Note: This would need to fetch the customer's user_id and check preferences
    // For now, this is a placeholder implementation
    if (status !== "New") {
      // In a real implementation, you'd fetch the job details to get customer_id
      // and then map to the user_id, and check notification preferences
      // For MVP, we're stubbing this out
    }
  },
  async updateJob(id, patch) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { customer: _c, files: _f, ...rest } = patch as any;
    void _c;
    void _f;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error } = await (supabase.from("jobs").update(rest as any) as any).eq("id", id);
    if (error) throw error;
  },
  async archiveJob(id) {
    const { error } = await supabase
      .from("jobs")
      .update({ archived_at: new Date().toISOString() } as never)
      .eq("id", id);
    if (error) throw error;
  },
  async unarchiveJob(id) {
    const { error } = await supabase
      .from("jobs")
      .update({ archived_at: null } as never)
      .eq("id", id);
    if (error) throw error;
  },
  async deleteJob(id) {
    // Best-effort cleanup of child rows (RLS allows staff). Failure here is non-fatal.
    await supabase.from("job_files").delete().eq("job_id", id);
    await supabase.from("job_activity").delete().eq("job_id", id);
    const { error } = await supabase.from("jobs").delete().eq("id", id);
    if (error) throw error;
  },
  async listFiles(jobId) {
    const { data } = await supabase
      .from("job_files")
      .select("*")
      .eq("job_id", jobId)
      .order("uploaded_at", { ascending: false });
    return (data ?? []) as unknown as JobFile[];
  },
  async addFile(jobId, file) {
    const path = `${jobId}/${Date.now()}-${file.name}`;
    const { error: upErr } = await supabase.storage.from("job-artwork").upload(path, file);
    if (upErr) throw upErr;
    const { data: pub } = supabase.storage.from("job-artwork").getPublicUrl(path);
    const { data, error } = await supabase
      .from("job_files")
      .insert({
        job_id: jobId,
        file_name: file.name,
        file_url: pub.publicUrl,
        file_type: "artwork",
      })
      .select("*")
      .single();
    if (error) throw error;
    return data as unknown as JobFile;
  },
  async updateFileType(fileId, fileType) {
    const { error } = await supabase
      .from("job_files")
      .update({ file_type: fileType })
      .eq("id", fileId);
    if (error) throw error;
  },
  async listActivity(jobId) {
    const { data } = await supabase
      .from("job_activity")
      .select("*")
      .eq("job_id", jobId)
      .order("created_at", { ascending: false });
    return (data ?? []) as unknown as JobActivity[];
  },
  async listCustomers() {
    const { data } = await supabase.from("customers").select("*").order("company_name");
    return (data ?? []) as unknown as Customer[];
  },
  async getCustomer(id) {
    const { data } = await supabase.from("customers").select("*").eq("id", id).maybeSingle();
    return (data as unknown as Customer | null) ?? null;
  },
};

const demoSource: DataSource = {
  async listJobs(filter) {
    let jobs = demoStore.jobs.map((j) => ({
      ...j,
      customer: demoStore.customers.find((c) => c.id === j.customer_id) ?? null,
    }));
    if (filter?.customer_id) jobs = jobs.filter((j) => j.customer_id === filter.customer_id);
    return jobs;
  },
  async getJob(id) {
    const j = demoStore.jobs.find((x) => x.id === id);
    if (!j) return null;
    return { ...j, customer: demoStore.customers.find((c) => c.id === j.customer_id) ?? null };
  },
  async createJob(input) {
    const job: Job = {
      id: uid(),
      customer_id: input.customer_id,
      job_title: input.job_title,
      product_type: input.product_type ?? null,
      print_size: input.print_size ?? null,
      quantity: input.quantity ?? 1,
      colours: input.colours ?? null,
      notes: input.notes ?? null,
      status: (input.status as JobStatus) ?? "New",
      due_date: input.due_date ?? null,
      cutting_tier_id: input.cutting_tier_id ?? null,
      shipping_method_id: input.shipping_method_id ?? null,
      gangsheet_design_id: input.gangsheet_design_id ?? null,
      payment_status: (input.payment_status ?? "pending") as "pending" | "confirmed" | "invoiced",
      assignee_profile_id: input.assignee_profile_id ?? null,
      drip_size_id: input.drip_size_id ?? null,
      metres: input.metres ?? null,
      quality_status: (input.quality_status ?? null) as "ok" | "warned" | "poor" | null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    demoStore.jobs.unshift(job);
    return job;
  },
  async updateJobStatus(id, status) {
    const j = demoStore.jobs.find((x) => x.id === id);
    if (j) {
      j.status = status;
      j.updated_at = new Date().toISOString();
    }
  },
  async updateJob(id, patch) {
    const j = demoStore.jobs.find((x) => x.id === id);
    if (j) Object.assign(j, patch, { updated_at: new Date().toISOString() });
  },
  async listFiles(jobId) {
    return demoStore.files.filter((f) => f.job_id === jobId);
  },
  async addFile(jobId, file) {
    const f: JobFile = {
      id: uid(),
      job_id: jobId,
      file_name: file.name,
      file_url: URL.createObjectURL(file),
      file_type: "artwork",
      uploaded_at: new Date().toISOString(),
    };
    demoStore.files.push(f);
    return f;
  },
  async updateFileType(fileId, fileType) {
    const f = demoStore.files.find((x) => x.id === fileId);
    if (f) f.file_type = fileType;
  },
  async listActivity(jobId) {
    return demoStore.activity.filter((a) => a.job_id === jobId);
  },
  async listCustomers() {
    return demoStore.customers;
  },
  async getCustomer(id) {
    return demoStore.customers.find((c) => c.id === id) ?? null;
  },
};

export function getDataSource(demoMode: boolean): DataSource {
  return demoMode ? demoSource : supaSource;
}
